<template>
  <v-container
    class="pa-0"
    fluid
  >
    <template v-if="getDefaultSchedule">
      <v-img
        id="logo"
        contain
        height="250px"
        :src="defaultSchedulePath"
      />
    </template>
    <template v-else>
      <preview
        :is-preview="previewMode"
        :schedule-manager="scheduleManager"
        :show="showPlayer"
        @progressFinished="progressFinished"
        @click.native="closeScreensaver()"
        @touchstart.native="closeScreensaver()"
      />
      <v-row id="textRenderingToCanvas" />
    </template>
  </v-container>
</template>

<script>
// Controllers
import { ScheduleLoader } from "./SchedulePage/ScheduleLoader";
import { PlayerScheduleManager } from "./Player/PlayerScheduleManager";

// Tools
const ft = require("./Tools/FileTools");
const mqtt = require("mqtt");

export default {
  components: {
    //
  },
  props: {
    license: {
      type: String,
      default: "",
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
    readyToPlay: {
      type: Boolean,
      default: false,
    },
    scheduleObject: {
      type: Object,
      default: () => {},
    },
    show: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      defaultSchedulePath: this.$store.state.config.system.defaultSchedule,
      defaultSchedule: false,
      showPlayer: false,
      scheduleManager: null,
      playerScheduleManager: null,
      alert: false,
      updSchedule: null,
      ignoreFirst: true,
      reconnectTimeout: null,
    };
  },
  computed: {
    getDefaultSchedule: {
      get() {
        return this.defaultSchedule;
      },
    },
  },
  watch: {
    readyToPlay: function (val) {
      if (val) {
        this.checkFile();
        this.scheduleMqtt();
      }
    },
    scheduleObject: function (val) {
      this.scheduleManager = val;
    },
    show: function (val) {
      this.showPlayer = val;
    },
  },
  created() {
    if (this.isElectron) {
      // IF ELECTRON RUN WE ACTIVATE THE FUNCTIONNALITIES ON REDUCE / MAXIMIZE / CLOSE
      this.appFunc = require("electron").remote;
      const windows = this.appFunc.getCurrentWindow();

      const process = require("process");
      const confPath = process.env.ALLUSERSPROFILE + "/DMCWare/JSON/config.json";

      this.initPlayer(confPath, windows);
    }
  },
  mounted() {
    this.$nextTick(function () {
      if (this.previewMode) {
        this.checkFile();
      }
    });
  },
  methods: {
    isElectron() {
      // Renderer process
      if (typeof window !== "undefined" && typeof window.process === "object" && window.process.type === "renderer") {
        return true;
      }

      // Main process
      if (typeof process !== "undefined" && typeof process.versions === "object" && !!process.versions.electron) {
        return true;
      }

      // Detect the user agent when the "nodeIntegration" option is set to true
      if (navigator.userAgent.indexOf() !== undefined) {
        if (
          typeof navigator === "object" &&
          typeof navigator.userAgent === "string" &&
          navigator.userAgent.indexOf("Electron") >= 0
        ) {
          return true;
        }
      }

      return false;
    },
    progressFinished() {
      this.$emit("progressFinished");
    },
    scheduleMqtt() {
      if (!this.$store.state.config.system.isCreation) {
        // In player mode we check new mqtt message
        this.connectMqtt();
        // Waiting with the default schedule displayed
        this.defaultSchedule = true;
      }
    },
    alertSchedule(message) {
      const moment = require("moment");
      const now = moment(moment().toDate("YYYY-MM-DD")).format("YYYY-MM-DD");
      const json = JSON.parse(message);
      if (now > json.dateFrom && now < json.dateTo) {
        this.playerScheduleManager = new PlayerScheduleManager(
          this.$store.state.config.dirs.root + "temp/alertDirectory/"
        );
        this.playerProcess(json);
        this.alert = true;
        this.defaultSchedule = false;
      }
    },
    connectMqtt() {
      const _this = this;

      clearTimeout(this.reconnectTimeout);
      const MQTTAddress =
        "mqtt://" + this.$store.state.config.url.mqtt.ip + ":" + this.$store.state.config.url.mqtt.port;
      let client = null;

      try {
        client = mqtt.connect(MQTTAddress, this.$store.state.config.url.mqtt);

        const topicName = "dmcware/" + this.$store.state.config.system.license;
        const topicAlert = topicName + "/alert";

        client.on("connect", () => {
          console.info("Mqtt connection succeeded");
        });
        client.on("disconnect", () => {
          console.warn("Mqtt is disconnected");
          _this.reconnectMqtt();
        });
        client.on("error", (err) => {
          console.error("Mqtt error: ", err);
          client.end();
          _this.reconnectMqtt();
        });
        client.subscribe(topicAlert, (err) => {
          if (err) {
            console.error("Mqtt alert subscription error: ", err);
            client.end();
            _this.reconnectMqtt();
          }
        });
        client.subscribe(topicName, (err) => {
          if (err) {
            console.error("Mqtt standard topic subscription error: ", err);
            client.end();
            _this.reconnectMqtt();
          }
        });
        client.on("message", function (topic, message) {
          if (_this.ignoreFirst) {
            // TODO: CHECK IF SCHEDULEJSON IS THE SAME ???
            console.info("First message ignored");
            _this.ignoreFirst = false;
          } else {
            // Clear possible previous timeout
            clearTimeout(_this.updSchedule);

            // New schedule received
            console.info("Message on mqtt topic " + topic + " : " + message.toString());
            // When new schedule published on mqtt we wait 5 secs and ???
            _this.updSchedule = setTimeout(() => {
              // We received an alert
              if (topic == topicAlert) {
                _this.alertSchedule(message);
              }
              // We received a standard schedule
              else if (!_this.alert) {
                _this.checkFile();
              }
            }, 5000);
          }
        });
      } catch (err) {
        console.error("Mqtt catch error :", err);
        if (client) {
          client.end();
        }
        _this.reconnectMqtt();
      }
    },
    reconnectMqtt() {
      const _this = this;

      this.ignoreFirst = false;

      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = setTimeout(function () {
        console.warn("Trying to reconnect mqtt");
        _this.connectMqtt();
      }, 180 * 1000);
    },
    async playerProcess(json = null) {
      // Find a new schedule
      await this.playerScheduleManager.retrieveLastFile();

      // If new schedule found
      if (this.playerScheduleManager.isThereNewProject()) {
        let currentProject = this.playerScheduleManager.getCurrentProjectRead();
        let alertProject = null;

        // If is alert message
        if (json !== null) {
          alertProject = this.$store.state.config.dirs.root + "temp/alertDirectory/projets/" + json.file;
        }

        // Load new schedule
        const scheduleLoader = new ScheduleLoader(
          currentProject,
          this.$store.state.user,
          this.previewMode,
          alertProject
        );

        // Create all objects
        this.scheduleManager = await scheduleLoader.make();

        // Load all objects
        this.scheduleManager.load();
      }

      this.showPlayer = true;
    },
    launch() {
      // this.defaultSchedule = false;
      if (this.previewMode) {
        this.playerScheduleManager = new PlayerScheduleManager(
          this.$store.state.config.dirs.root +
            "temp/" +
            this.$store.state.user.socId +
            "/" +
            this.$store.state.user.userId +
            "/readingPreview/"
        );
      } else {
        this.playerScheduleManager = new PlayerScheduleManager(
          this.$store.state.config.dirs.app + "temp/readingDirectory/"
        );
      }

      this.playerProcess();
    },
    getHeight() {
      return window.innerHeight;
    },
    getWidth() {
      return window.innerWidth;
    },
    checkFile() {
      const _this = this;

      // TODO: CHECK ALSO IF DIRECTORY IS EMPTY TO WAIT DOWNLOAD OR EXTRACT BY SERVICE + EXPLORER
      ft.exists(_this.$store.state.config.dirs.app + "temp/readingDirectory/vision.lock")
        .then((response) => {
          // if lock not found we can display the new schedule
          if (!response) {
            console.info("Vision lock not found load schedule !");
            _this.defaultSchedule = false;
            _this.launch();
          } else {
            console.info("Vision lock still exist retrying in 5s");
            setTimeout(function () {
              _this.checkFile();
            }, 5000);
          }
        })
        .catch((err) => {
          console.error(err);
          _this.defaultSchedule = false;
          _this.launch();
        });
    },
    initPlayer(confPath, windows) {
      const _this = this;
      clearTimeout(this.initTimeout);

      // Seek in ProgramData/DMCWare/JSON/config.json
      ft.read(confPath)
        .then((config) => {
          // If position is not defined in conf file
          if (config.app.position === undefined) {
            config.app.position = ["top", "left"];
          }
          // If resolution is not defined in conf file
          if (config.app.resolution === undefined) {
            config.app.resolution = [];
          }
          // If statsEnable is not defined in conf file set to true by default
          if (config.app.statsEnable === undefined) {
            config.app.statsEnable = true;
          }

          // Set license from conf file in ProgramData
          this.$store.dispatch("setLicense", config.app.license).config;

          // Resize the player to the corresponding size
          this.$store.dispatch("setResolution", config.app.resolution).config;

          // Define appdata directory to watch service temp directory
          this.$store.dispatch("setAppDir", config.path.player + "/Programs/DMCWare/").config;

          // Define player mode to screensaver if necessary (use to close player on click)
          this.$store.dispatch("setScreensaverMode", config.app.screensaver).config;

          // Define if the player send stats to the server
          this.$store.dispatch("setStatsEnable", config.app.statsEnable).config;

          // Let's go for player page watcher
          this.readyToPlay = true;

          // Define the window size
          if (this.$store.state.config.system.resolution.length > 1) {
            windows.setResizable(true);
            windows.setSize(
              parseInt(this.$store.state.config.system.resolution[0], 10),
              parseInt(this.$store.state.config.system.resolution[1], 10)
            );
          }

          // Disable resize to be sure that user don't modify it
          windows.setResizable(false);

          // Define position of the player windows (top | left | bottom | right)
          if (config.app.position.length !== 0 && config.app.resolution.length > 1) {
            this.$store.dispatch("setPosition", config.app.position).config;

            this.$store.state.config.system.position.forEach((pos) => {
              const x = windows.getPosition()[0];
              const y = windows.getPosition()[1];
              const posX = window.screen.width - parseInt(this.$store.state.config.system.resolution[0], 10);
              const posY = window.screen.height - parseInt(this.$store.state.config.system.resolution[1], 10);

              switch (pos) {
                case "top":
                  windows.setPosition(x, 0);
                  break;
                case "bottom":
                  windows.setPosition(x, posY);
                  break;
                case "left":
                  windows.setPosition(0, y);
                  break;
                case "right":
                  windows.setPosition(posX, y);
                  break;

                default:
                  break;
              }
            });
          } else {
            windows.setPosition(0, 0);
          }
        })
        .catch((err) => {
          // If we can't read the conf file
          console.error("Can't read conf file retry in one minute");
          console.error(err);

          this.initTimeout = setTimeout(function () {
            _this.initPlayer(confPath, windows);
          }, 60 * 1000);
        });
    },
    close() {
      if (this.isElectron()) {
        this.appFunc.getCurrentWindow().close();
      }
    },
    closeScreensaver() {
      if (this.$store.state.config.system.screensaverMode) {
        this.close();
      }
    },
  },
};
</script>

<style>
/**
    Text rendering container
  */
#textRenderingToCanvas {
  position: absolute;
  top: -9999px;
  left: -9999px;

  background-color: transparent;
  font-size: 400%;
  white-space: nowrap;
  word-wrap: none;
  opacity: 0;

  /* Set properties like v-application on CreationPage */
  font-family: "Roboto", sans-serif;
  line-height: 1.5;
}
</style>
